import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  Info,
  Loader2,
  Share2,
  ShieldCheck,
  Truck,
} from 'lucide-react';
import type { Product, ProductPosition } from '@/api/products';
import { cn } from '@/lib/utils';
import { DirectSaleSheet } from './DirectSaleSheet';
import { formatMoney } from './format-money';
import { composeMockupImage } from '@/lib/mockupComposite';
import { useCanvasStateManager } from '@/stores/canvasStateManager';
import { useProductSelectionStore } from '@/stores/productSelectionStore';
import { useDirectSaleMockupInput } from './useDirectSaleMockupInput';
import { useDirectSaleMethod } from './useDirectSaleMethod';
import { positionMethodName, positionPrice } from './direct-sale-methods';

type ShareOutcome = 'saved' | 'failed';

/** Surface behind the product in the shared image — the editor's ground. */
const SHARE_BACKGROUND = '#E4E7F0';

/**
 * The phone's "more" sheet behind the three dots: product details and
 * sharing up front, delivery and quality below — what a customer wants to
 * know before buying, without leaving the product.
 *
 * Sharing hands on a picture: the product with the design on it, composed
 * the way the preview shows it. No link — the frame's own URL carries the
 * shop signature and is nobody else's business. The picture is composed as
 * soon as the sheet opens, not on the tap: Safari only lets `share()` run
 * inside the user gesture, and loading the layers takes longer than that.
 */
export function DirectSaleMoreSheet({
  product,
  sizes,
  onClose,
}: {
  product: Product;
  sizes: { id: string; name: string }[];
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState<'menu' | 'details'>('menu');
  const [shareOutcome, setShareOutcome] = useState<ShareOutcome | null>(null);
  const money = (amount: string | number) =>
    formatMoney(amount, 'EUR', i18n.language);

  // The view the customer is looking at — or, if that one is empty, the
  // first one that carries a design.
  const activePosition = useProductSelectionStore((s) => s.activePosition);
  const masterPositions = useCanvasStateManager((s) => s.masterPositions);
  const shareSlug =
    (masterPositions[activePosition]?.preview
      ? activePosition
      : (product.positions ?? []).find((p) => masterPositions[p.slug]?.preview)
          ?.slug) ?? activePosition;
  const designUrl = masterPositions[shareSlug]?.preview ?? null;
  const mockup = useDirectSaleMockupInput(product, shareSlug);

  const [picture, setPicture] = useState<File | null>(null);
  const [composing, setComposing] = useState(true);
  const mockupRef = useRef(mockup);
  mockupRef.current = mockup;
  const templateId = mockup.template?.id ?? null;
  useEffect(() => {
    const { template, ...rest } = mockupRef.current;
    if (!template) {
      setComposing(false);
      return;
    }
    let live = true;
    setComposing(true);
    composeMockupImage({
      template,
      ...rest,
      designUrl,
      frameBackground: SHARE_BACKGROUND,
    }).then(
      (blob) => {
        if (!live) return;
        setPicture(new File([blob], 'design.png', { type: 'image/png' }));
        setComposing(false);
      },
      () => live && setComposing(false)
    );
    return () => {
      live = false;
    };
  }, [templateId, designUrl, mockup.backgroundColor]);

  /** No share sheet here (a desktop, a frame without permission): save it. */
  const save = (file: File) => {
    try {
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      setShareOutcome('saved');
    } catch {
      setShareOutcome('failed');
    }
  };

  // Synchronous up to share(): still inside the tap.
  const share = () => {
    if (!picture) {
      setShareOutcome('failed');
      return;
    }
    const data = { title: product.name, files: [picture] };
    // Inside a shop's iframe this needs allow="web-share" on the frame.
    if (navigator.canShare?.(data)) {
      navigator.share(data).catch((error: { name?: string }) => {
        // The customer closed the share sheet: nothing to report.
        if (error?.name !== 'AbortError') save(picture);
      });
      return;
    }
    save(picture);
  };

  const tile =
    'flex h-[92px] flex-col items-center justify-center gap-2 rounded-card bg-paper-2 text-[14px] font-bold text-ink';
  const pricedPositions = (product.positions ?? []).filter(
    (p) => p.printSurcharge != null
  );
  // Prices and names follow the decoration method the customer chose.
  const { effectiveId: methodId } = useDirectSaleMethod(product);
  const positionLabel = (position: ProductPosition) => {
    const method = positionMethodName(position, methodId);
    return method
      ? t('directSale.mobile.decorationOn', {
          method,
          position: position.name,
        })
      : t('directSale.mobile.designOn', { position: position.name });
  };
  const description = product.description || product.shortDescription;

  if (view === 'details') {
    return (
      <DirectSaleSheet
        title={t('directSale.mobile.productDetails')}
        modal
        tall
        onClose={onClose}
        headerAction={
          <button
            type="button"
            aria-label={t('directSale.mobile.back')}
            onClick={() => setView('menu')}
            className="flex h-12 w-12 flex-none items-center justify-center text-ink"
          >
            <ChevronLeft className="h-6 w-6" strokeWidth={2.2} />
          </button>
        }
      >
        <div className="flex flex-col gap-5 px-[18px] pb-5 pt-1 text-[14px] leading-relaxed text-ink-2">
          <div>
            <h3 className="font-display text-[17px] font-bold text-ink">
              {product.name}
            </h3>
            {product.oneLineDescription && (
              <p className="mt-0.5 text-ink-3">{product.oneLineDescription}</p>
            )}
          </div>
          {description && <p className="whitespace-pre-line">{description}</p>}
          {product.material && (
            <div>
              <h4 className="text-[13px] font-bold text-ink">
                {t('directSale.mobile.material')}
              </h4>
              <p>{product.material}</p>
            </div>
          )}
          {sizes.length > 0 && (
            <div>
              <h4 className="text-[13px] font-bold text-ink">
                {t('directSale.mobile.sizes')}
              </h4>
              <p>{sizes.map((size) => size.name).join(' · ')}</p>
            </div>
          )}
          {(product.recommendedSalesPrice != null ||
            pricedPositions.length > 0) && (
            <div>
              <h4 className="text-[13px] font-bold text-ink">
                {t('directSale.mobile.prices')}
              </h4>
              <dl className="mt-1 divide-y divide-line rounded-card border border-line">
                {product.recommendedSalesPrice != null && (
                  <div className="flex justify-between px-3.5 py-2.5 text-ink">
                    <dt>{product.name}</dt>
                    <dd className="font-semibold">
                      {money(product.recommendedSalesPrice)}
                    </dd>
                  </div>
                )}
                {pricedPositions.map((position) => (
                  <div
                    key={position.id}
                    className="flex justify-between px-3.5 py-2.5"
                  >
                    <dt>{positionLabel(position)}</dt>
                    <dd className="font-semibold text-ink">
                      +{money(positionPrice(position, methodId) ?? 0)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </DirectSaleSheet>
    );
  }

  return (
    <DirectSaleSheet
      title={t('directSale.mobile.moreOptions')}
      modal
      onClose={onClose}
    >
      <div className="flex flex-col gap-3 px-3 pb-3 pt-2">
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            className={tile}
            onClick={() => setView('details')}
          >
            <Info className="h-6 w-6" />
            {t('directSale.mobile.productDetails')}
          </button>
          <button
            type="button"
            className={cn(tile, composing && 'opacity-60')}
            disabled={composing}
            onClick={share}
          >
            {composing ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <Share2 className="h-6 w-6" />
            )}
            {t('directSale.mobile.share')}
          </button>
        </div>
        {shareOutcome && (
          <p
            role="status"
            className={cn(
              'text-center text-[13px] font-semibold',
              shareOutcome === 'saved' ? 'text-ink' : 'text-danger'
            )}
          >
            {shareOutcome === 'saved'
              ? t('directSale.mobile.pictureSaved')
              : t('directSale.mobile.shareFailed')}
          </p>
        )}
        <div className="divide-y divide-line rounded-card bg-paper-2">
          <div className="flex items-center gap-3.5 px-4 py-3.5">
            <Truck className="h-6 w-6 flex-none text-ink" />
            <div>
              <div className="text-[14px] font-bold text-ink">
                {t('directSale.checkout.trustShippingTitle')}
              </div>
              <div className="text-[13px] text-ink-3">
                {t('directSale.checkout.trustShippingSub')}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3.5 px-4 py-3.5">
            <ShieldCheck className="h-6 w-6 flex-none text-ink" />
            <div>
              <div className="text-[14px] font-bold text-ink">
                {t('directSale.checkout.trustQualityTitle')}
              </div>
              <div className="text-[13px] text-ink-3">
                {t('directSale.checkout.trustQualitySub')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DirectSaleSheet>
  );
}
